import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import _ from 'lodash';

import { Session } from '../../../models/Sessions/Session';
import { ConfigService } from '../../../services/config.service';
import { isValidIdea } from '../FilterHelper';

const TIMEOUT_UPDATE_SORT_ORDER = 10000;

@Component({
  selector: 'app-topic-filter',
  templateUrl: './topic-filter.component.html',
  styleUrls: ['./topic-filter.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TopicFilterComponent implements OnInit {
  @Input() allSessions: Session[];

  @ViewChild(VirtualScrollerComponent, { static: true })
  scroll: VirtualScrollerComponent;

  @ViewChild('divParent', { static: true }) divParent: ElementRef;
  private _sortedSessions: Session[] = [];
  private timeoutHandle: any = null;
  private componentTriggeredFilterUpdate = false;

  @Input() set filteredSessions(sessions: Session[]) {
    const initialUpdate = this.getFilteredSessions() === null;

    this._filteredSessions = sessions;

    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
    }

    if (initialUpdate || !this.componentTriggeredFilterUpdate) {
      // initial call without timeout
      this.updateSortedSessions();
    } else {
      // delay update so that list does not order itself
      this.timeoutHandle = setTimeout(
        () => this.updateSortedSessions(),
        TIMEOUT_UPDATE_SORT_ORDER,
      );
      this.componentTriggeredFilterUpdate = false;
    }

    if (this.initialized) {
      this.initializeDropdownList();
    }
  }

  @Output() updatedFilter = new EventEmitter();

  dropdownList: any[] = [];
  _filteredSessions = null;
  selectedTopics: object = {};
  initialized = false;

  selectedSessions: any[] = [];

  selectedItems: object[] = [];
  height = '400px';
  width = '400px';

  constructor(public config: ConfigService) {}

  // sort by (selected, filtered, number of ideas in session
  onSelectionChanged(changed) {
    for (const session of _.keys(this.selectedTopics)) {
      this.selectedTopics[session] = false;
    }

    for (const session of changed) {
      this.selectedTopics[session] = true;
    }

    this.updateFilter();
  }

  @HostListener('window:resize', ['$event'])
  afterResize() {
    this.height = this.divParent.nativeElement.offsetHeight + 'px';
    this.width = this.divParent.nativeElement.offsetWidth + 'px';
    this.scroll.refresh();
  }

  get placeHolderString() {
    if (this.allSessions.length > this.getFilteredSessions().length) {
      return `Select or Search Topics (${this.getFilteredSessions().length} of ${this.allSessions.length})`;
    } else {
      return `Select or Search Topics (${this.allSessions.length})`;
    }
  }

  updateSortedSessions() {
    this._sortedSessions = _.sortBy(this.allSessions, session => {
      if (this.isSelected(session)) {
        return -2;
      } else if (this.isFilteredSession(session)) {
        return -1;
      } else {
        return session.data.ideas.filter(idea => idea.published).length;
      }
    });
  }

  getSortedSessions() {
    return this._sortedSessions;
  }

  toggleTopic(session) {
    if (this.isSelected(session) || this.isFilteredSession(session)) {
      this.selectedTopics[session._id] = !this.selectedTopics[session._id];
      this.updateSelectedItems();
      this.updateFilter();
    }

    this.selectedSessions = [];
    for (const _session of _.keys(this.selectedTopics)) {
      if (this.selectedTopics[_session]) {
        this.selectedSessions.push(_session);
      }
    }
  }

  getAllSessions() {
    return this.allSessions;
  }

  initializeDropdownList() {
    this.dropdownList = this.getAllSessions()
      .filter(
        session => this.isFilteredSession(session) || this.isSelected(session),
      )
      .map(session => {
        return { id: session._id, name: session.config.title };
      });
  }

  updateSelectedItems() {
    this.selectedItems = this.dropdownList.filter(
      item => this.selectedTopics[item.item_id],
    );
  }

  isSelected(session) {
    return this.selectedTopics[session._id];
  }

  isFilteredSession(session) {
    if (this._filteredSessions) {
      return (
        this._filteredSessions
          .map(filteredSession => filteredSession._id)
          .indexOf(session._id) >= 0
      );
    } else {
      return true;
    }
  }

  ngOnInit() {
    this.allSessions.map(session => {
      this.selectedTopics[session._id] = false;
    });

    this.dropdownList = this.allSessions.map(session => {
      return {
        disabled: true,
        item_id: session._id,
        item_text: session.config.title,
      };
    });

    this.initializeDropdownList();

    this.height = this.divParent.nativeElement.offsetHeight + 'px';
    this.width = this.divParent.nativeElement.offsetWidth + 'px';

    this.initialized = true;
  }

  updateFilter() {
    const selection = _.keys(this.selectedTopics).filter(
      id => this.selectedTopics[id],
    );
    this.componentTriggeredFilterUpdate = true;
    this.updatedFilter.emit(selection);
  }

  getNumberOfIdeasInSession(session: Session) {
    return session.data.ideas.filter(isValidIdea).length;
  }

  getFilteredSessions() {
    return this._filteredSessions;
  }

  clearTopic(sessionId: any) {
    const selectedSession = _.find(
      this.allSessions,
      session => session._id === sessionId,
    );
    this.toggleTopic(selectedSession);
  }
}
